import { createSelector } from '@reduxjs/toolkit'
import { useEffect, useMemo } from 'react'

import { ANY_RESOURCE_KIND, queryView, useWindowShell, type QueryView, type SectionView, type WindowView } from '@krp/client-runtime'
import { platformConnectors, platformMyResources, type MyResource } from '@krp/platform-ui'

import { CONNECTION_FORMS } from '../composition/connectionForms'
import { useAppDispatch, useAppSelector } from './hooks'
import type { RootState } from './types'

/** Префикс id вкладок и панели раздела (`tabId`/`panelId` ui-kit): полоса одна на странице. */
export const SECTION_TABS_PREFIX = 'shell-section'

/** Вкладка раздела глазами оболочки: куда ведёт и как подписана. */
export interface SectionTab {
  /** Identity окна вкладки — ключ и id элемента вкладки. */
  id: string
  kind: string
  path: string
  /** Имя ресурса или, если у раздела несколько окон одного вида, чем вкладка занята. */
  title: string
  /** Имя ресурса, когда `title` — занятие вкладки и ресурсов вида несколько; иначе `null`. */
  subtitle: string | null
  /** Метка вида ресурса («1С», «AQSI») — от формы пакета вида; у собственной вкладки входа — `null`. */
  badge: string | null
  /** Цвет вида ресурса (`data-source`) для метки; у вкладки без ресурса и у вида без цвета — `null`. */
  tone: string | null
  resourceId: string | null
  /**
   * Вид ресурса не умеет раздел (таблица замечаний 13.10.2026, п. 7): вкладка видна с замком и не выбирается — человек
   * видит, что источник есть, но здесь работать нечем, а не гадает, куда он пропал. Окна и ресурса у неё нет.
   */
  locked: boolean
}

/** Источник раздела: один ресурс со всеми окнами раздела, которые он умеет (для плиток и полосы «Источник»). */
export interface SectionResource {
  resource: MyResource
  /** Окно, с которого источник открывается: первая его вкладка в разделе. */
  kind: string
  path: string
  /** Подписи окон раздела этого источника по порядку («Чеки», «Инциденты продаж»). */
  labels: readonly string[]
}

export interface SectionTabs {
  /** Вкладки ресурсов известны (или их нет совсем) — `ready`; иначе загрузка или отказ «моих ресурсов». */
  query: QueryView
  tabs: readonly SectionTab[]
  /** Те же ресурсы, но по одному на источник — выбор источника плитками (`section.pickResource`). */
  resources: readonly SectionResource[]
}

type MyResourcesResult = ReturnType<ReturnType<typeof platformMyResources.select>>

const selectConnectors = platformConnectors.select()

const NO_CAPABILITIES: readonly string[] = []
const NO_RESULTS: readonly MyResourcesResult[] = []
const NO_TABS: readonly SectionTab[] = []
const NO_RESOURCES: readonly SectionResource[] = []
const READY: QueryView = { status: 'ready', refreshing: false, error: null }

/** Подходит ли ресурс вкладке: вид совпал или окно объявлено для любого вида (`ANY_RESOURCE_KIND`). */
const fits = (resourceKind: string, tabResourceKind: string): boolean =>
  tabResourceKind === ANY_RESOURCE_KIND || resourceKind === tabResourceKind

/**
 * Вкладки раздела (UI-SECTIONS): собственная вкладка входа и по вкладке на
 * каждый ресурс, который умеет окно раздела, — из «моих ресурсов» с правом
 * окна. Вид ресурса здесь — данные (`resource.kind === tab.resourceKind`), а
 * не ветвление: новый вид приходит своими окнами, оболочка не меняется.
 */
export function useSectionTabs(section: SectionView | undefined): SectionTabs {
  const shell = useWindowShell()
  const dispatch = useAppDispatch()
  const capabilities = useMemo(
    () => (section === undefined ? NO_CAPABILITIES : [...new Set(section.tabs.map((tab) => tab.capability))]),
    [section],
  )

  // Подписка на «мои ресурсы» по каждому праву раздела: сколько прав — столько списков, их число меняется с разделом.
  useEffect(() => {
    const subscriptions = capabilities.map((capability) => dispatch(platformMyResources.initiate({ capability })))
    return () => {
      for (const subscription of subscriptions) subscription.unsubscribe()
    }
  }, [capabilities, dispatch])

  const select = useMemo(() => {
    if (capabilities.length === 0) return () => NO_RESULTS
    const selectors = capabilities.map((capability) => platformMyResources.select({ capability }))
    return createSelector(selectors, (...results: readonly MyResourcesResult[]) => results)
  }, [capabilities])
  const results = useAppSelector((state: RootState) => select(state))

  // Виды ресурсов объявляет сервер (манифесты, ADR-22): плитки «недоступно» — по ним, а не по списку форм в клиенте.
  useEffect(() => {
    const subscription = dispatch(platformConnectors.initiate())
    return () => subscription.unsubscribe()
  }, [dispatch])
  const connectors = useAppSelector(selectConnectors).data

  return useMemo<SectionTabs>(() => {
    if (section === undefined) return { query: READY, tabs: NO_TABS, resources: NO_RESOURCES }
    const resources = new Map<string, readonly MyResource[]>()
    let query: QueryView = READY
    capabilities.forEach((capability, index) => {
      const result = results[index]
      if (result === undefined) return
      const view = queryView(result, () => false)
      if (view.status !== 'ready' && query.status === 'ready') query = view
      // Источники — в общем порядке (решение владельца 03.11.2026): по виду, внутри вида — по названию.
      if (result.data !== undefined) resources.set(capability, CONNECTION_FORMS.sortSources(result.data))
    })

    // Сколько окон раздела у вида и сколько его ресурсов: подпись вкладки называет то, что их различает.
    const perKind = new Map<string, number>()
    for (const tab of section.tabs) perKind.set(tab.resourceKind, (perKind.get(tab.resourceKind) ?? 0) + 1)
    const resourcesOfKind = new Map<string, Set<string>>()
    for (const tab of section.tabs) {
      for (const resource of resources.get(tab.capability) ?? []) {
        if (!fits(resource.kind, tab.resourceKind)) continue
        const ids = resourcesOfKind.get(tab.resourceKind) ?? new Set<string>()
        ids.add(resource.id)
        resourcesOfKind.set(tab.resourceKind, ids)
      }
    }

    const tabs: SectionTab[] = []
    const sources = new Map<string, { resource: MyResource; kind: string; path: string; labels: string[] }>()
    if (section.homeTab !== null) {
      tabs.push({
        id: section.homeKind,
        kind: section.homeKind,
        path: section.path,
        title: section.homeTab,
        subtitle: null,
        badge: null,
        tone: null,
        resourceId: null,
        locked: false,
      })
    }
    // Собственные вкладки раздела («Настройка»): без ресурса, сразу после вкладки входа.
    for (const own of section.ownTabs) {
      tabs.push({
        id: own.kind,
        kind: own.kind,
        path: shell.pathOf(own.kind),
        title: own.label,
        subtitle: null,
        badge: null,
        tone: null,
        resourceId: null,
        locked: false,
      })
    }
    // Вкладки ресурсов — по источнику в общем порядке (решение владельца 03.11.2026): AQSI, 1С, «Собственная», внутри вида по
    // названию; окна одного источника идут подряд в своём порядке раздела. Сортировка устойчивая.
    const resourceTabs: { tab: SectionTab; resource: MyResource }[] = []
    for (const tab of section.tabs) {
      // Несколько окон вида — вкладка называется занятием («Чеки»), имя ресурса — подписью, если ресурсов вида больше одного.
      const several = (perKind.get(tab.resourceKind) ?? 0) > 1
      const manyResources = (resourcesOfKind.get(tab.resourceKind)?.size ?? 0) > 1
      for (const resource of resources.get(tab.capability) ?? []) {
        if (!fits(resource.kind, tab.resourceKind)) continue
        const view = shell.match(shell.pathOf(tab.kind, { rid: resource.id }))
        const source = sources.get(resource.id)
        if (source === undefined) {
          sources.set(resource.id, { resource, kind: tab.kind, path: shell.pathOf(tab.kind, { rid: resource.id }), labels: [tab.label] })
        } else if (!source.labels.includes(tab.label)) {
          source.labels.push(tab.label)
        }
        resourceTabs.push({
          resource,
          tab: {
            id: view?.identity ?? `${tab.kind}:${resource.id}`,
            kind: tab.kind,
            path: shell.pathOf(tab.kind, { rid: resource.id }),
            title: several ? tab.label : resource.displayName,
            subtitle: several && manyResources ? resource.displayName : null,
            badge: CONNECTION_FORMS.shortTitleOf(resource.kind),
            tone: CONNECTION_FORMS.toneOf(resource.kind),
            resourceId: resource.id,
            locked: false,
          },
        })
      }
    }
    resourceTabs.sort((left, right) => CONNECTION_FORMS.compareSources(left.resource, right.resource))
    for (const { tab } of resourceTabs) tabs.push(tab)
    // Виды, которые объявил сервер, без окна в разделе — вкладкой с замком в конце полосы; у раздела без окон ресурсов их нет.
    if (section.tabs.length > 0 && !perKind.has(ANY_RESOURCE_KIND)) {
      const kinds = (connectors ?? []).map((manifest) => manifest.kind).sort(CONNECTION_FORMS.compareKinds)
      for (const kind of kinds) {
        if (perKind.has(kind)) continue
        tabs.push({
          id: `locked:${kind}`,
          kind,
          path: section.path,
          title: 'недоступно',
          subtitle: null,
          badge: CONNECTION_FORMS.shortTitleOf(kind),
          tone: CONNECTION_FORMS.toneOf(kind),
          resourceId: null,
          locked: true,
        })
      }
    }
    const ordered = [...sources.values()].sort((left, right) => CONNECTION_FORMS.compareSources(left.resource, right.resource))
    return { query, tabs, resources: ordered }
  }, [capabilities, connectors, results, section, shell])
}

/**
 * Разделы без полосы вкладок над окнами (UI-RMK-FLOW 3.4): источник «РМК» выбирают плитки, а в продаже место
 * экрана нужнее — сменить источник можно из «Действий».
 */
const SECTIONS_WITHOUT_STRIP: ReadonlySet<string> = new Set(['rmk'])

export const hasTabStrip = (sectionId: string): boolean => !SECTIONS_WITHOUT_STRIP.has(sectionId)

/**
 * Окно на экране — вкладка раздела: окно ресурса раздела или вход со своей
 * вкладкой («Общий каталог»). Вход без своей вкладки — переход к вкладке, полосы
 * у него нет; карточка внутри раздела — тоже не вкладка.
 */
export function isSectionTabWindow(section: SectionView, view: WindowView): boolean {
  return (
    (view.kind === section.homeKind && section.homeTab !== null) ||
    section.ownTabs.some((tab) => tab.kind === view.kind) ||
    section.tabs.some((tab) => tab.kind === view.kind)
  )
}
